import { useEffect, useState } from 'react';
import {
  DropDown,
  Flex,
  FlexColumn,
  FormControls,
  InfoTooltip,
} from '@elementary/common-ui/components';
import { AnomalySensitivity } from '@elementary/common-ui/types';
import { Controller, UseFormReturn } from 'react-hook-form';
import PercentageThresholdField from 'shared/components/PercentageThresholdField';
import {
  AnomalyDirectionField,
  DetectionModeField,
  DetectionPeriodField,
} from 'shared/components/VolumeAnomalyConfigSections';
import { anomalyDirectionOptions } from 'shared/constants';
import { AddTestForm, VolumeDetectionMode } from 'shared/types';
import { sensitivityOptions } from 'shared/constants';
import { DEFAULT_ANOMALY_TRAINING_PERIOD } from '../constants';
import SeveritySelector from '../SeveritySelector';
import TestMetadataForm from './shared/TestMetadataForm';
import WizardTrainingPeriodSection from './shared/WizardTrainingPeriodSection';

type Props = {
  methods: UseFormReturn<AddTestForm, any, undefined>;
};

const TablesVolumeAnomaliesCloud = ({ methods }: Props) => {
  const [expanded, setExpanded] = useState(true);
  const mode: VolumeDetectionMode =
    methods.watch('volumeDetectionMode') ?? 'anomaly_detection';

  const anomalyDirection = methods.watch('anomalyConfig.anomalyDirection');

  const handleModeChange = (next: VolumeDetectionMode) => {
    methods.setValue('volumeDetectionMode', next);
  };

  useEffect(() => {
    methods.setValue(
      'anomalyConfig.anomalyDirection',
      anomalyDirectionOptions[0].value as 'BOTH' | 'DROP' | 'SPIKE',
    );
    methods.setValue(
      'anomalyConfig.sensitivity',
      sensitivityOptions[1].value as AnomalySensitivity,
    );
    methods.setValue('anomalyConfig.detectionPeriod', {
      period: 'day',
      count: 2,
    });
    methods.setValue(
      'anomalyConfig.trainingPeriod',
      DEFAULT_ANOMALY_TRAINING_PERIOD,
    );
    methods.setValue('volumeDetectionMode', 'anomaly_detection');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormControls.TestAccordion
      summary="Volume anomalies"
      expanded={expanded}
      onChange={(_, val) => setExpanded(val)}
    >
      <FlexColumn sx={{ gap: 3, p: 3 }}>
        <DetectionModeField mode={mode} onChange={handleModeChange} />

        <AnomalyDirectionField
          control={methods.control}
          name="anomalyConfig.anomalyDirection"
        />

        {mode === 'percentage_thresholds' && (
          <FormControls.FieldsWrapper style={{ gap: '8px' }}>
            <Flex sx={{ alignItems: 'center', gap: '4px' }}>
              <FormControls.FormTitle>
                Fixed percentage thresholds
              </FormControls.FormTitle>
              <InfoTooltip
                size={16}
                title={
                  <>
                    Compared to the previous measurement.
                    <br />
                    Example: a 3% drop threshold fails when 100 → 97.
                  </>
                }
              />
            </Flex>

            <PercentageThresholdField
              control={methods.control}
              anomalyDirection={anomalyDirection}
              spikeFieldName="anomalyConfig.spikeThreshold"
              dropFieldName="anomalyConfig.dropThreshold"
              shouldUnregister={false}
            />
          </FormControls.FieldsWrapper>
        )}

        {mode === 'anomaly_detection' && (
          <FormControls.FieldsWrapper style={{ gap: '8px' }}>
            <FormControls.FormTitle>Sensitivity</FormControls.FormTitle>

            <FormControls.Row style={{ gap: '8px', flexWrap: 'wrap' }}>
              <FormControls.FormText>
                Adjust test thresholds by setting the sensitivity level to be
              </FormControls.FormText>
              <Controller
                control={methods.control}
                name="anomalyConfig.sensitivity"
                render={({ field }) => (
                  <DropDown
                    sortOptions={false}
                    options={sensitivityOptions}
                    {...field}
                  />
                )}
              />
            </FormControls.Row>
          </FormControls.FieldsWrapper>
        )}

        <DetectionPeriodField
          control={methods.control}
          register={methods.register}
          countName="anomalyConfig.detectionPeriod.count"
          periodName="anomalyConfig.detectionPeriod.period"
        />

        <WizardTrainingPeriodSection methods={methods} />

        <FormControls.FieldsWrapper>
          <Controller
            name="severity"
            control={methods.control}
            defaultValue={{ type: 'warn' }}
            render={({ field }) => <SeveritySelector {...field} />}
          />
        </FormControls.FieldsWrapper>

        <TestMetadataForm methods={methods} />
      </FlexColumn>
    </FormControls.TestAccordion>
  );
};

export default TablesVolumeAnomaliesCloud;
